import log from "loglevel";
import { calculateSignature } from "./calculateHMACToken";
import { UAParser } from "ua-parser-js";
import { v4 as uuid_v4 } from "uuid";
import { getDefaultLanguage } from "./GlobalFunctions";
import { publicIpv4 } from "public-ip";

class WebSocketManager {
  constructor(uri, kioskUnlockEventHandlerMap, wallID) {
    log.debug("+WebsocketManager_constructor(TRACE)");
    log.info(
      "WebsocketManager_constructor() => uri: %s, kioskUnlockEventHandlerMap: %o, wallID: %s",
      uri,
      kioskUnlockEventHandlerMap,
      wallID
    );
    let parser = new UAParser();
    let name =
      parser.getOS().name !== undefined
        ? parser.getOS().name.split(" ").join("-")
        : "";
    const correlation_id = uuid_v4();
    const date = new Date();
    const secretKey = window.atob(localStorage.getItem("SecretKey"));
    const signature = calculateSignature(
      localStorage.getItem("orgID"),
      date.toISOString().slice(0, 19) + ".000Z",
      secretKey,
      "POST",
      "/security/access-key-authentication/login",
      correlation_id
    );

    const getIpAddress = async () => {
      let ipAddress = await publicIpv4();
      if (typeof ipAddress === "string") {
        return ipAddress;
      } else {
        return undefined;
      }
    };

    getIpAddress().then((ipAddress) => {
      this.ws = new WebSocket(
        uri +
          "?type=browser&nep-enterprise-unit=" +
          localStorage.getItem("siteID") +
          "&nep-organization=" +
          localStorage.getItem("orgID") +
          "&id=" +
          this.getDeviceTerminalID() +
          "&wallid=" +
          localStorage.getItem("wallID") +
          "&serial=" +
          parser.getBrowser().version +
          "&device=" +
          name +
          "&key=" +
          localStorage.getItem("SharedKey") +
          "&hash=" +
          signature +
          "&date=" +
          date.toISOString() +
          "&ipAddress=" +
          ipAddress +
          "&nep-correlation-id=" +
          correlation_id
      );
      log.info(
        "WebsocketManger_constructor() => new websocket created and the URL is: " +
          this.ws.url
      );
      this.wallID = wallID;
      this.kioskUnlockEventHandlerMap = kioskUnlockEventHandlerMap;

      this.ws.addEventListener("open", (event) => {
        log.debug("+WebsocketManager_addEventListener(TRACE)");
        log.info(
          "WebsocketManager_addEventListener() => websocket connection opened"
        );
        this.kioskUnlockEventHandlerMap.get("open")();
        log.debug("-WebsocketManager_addEventListener(TRACE)");
      });

      this.ws.addEventListener("close", (event) => {
        log.debug("+WebsocketManager_addEventListener(TRACE)");
        log.info(
          "WebsocketManager_addEventListener() => websocket connection closed, trying to re-establish the connectivity"
        );
        this.kioskUnlockEventHandlerMap.get("close")();
        log.debug("-WebsocketManager_addEventListener(TRACE)");
      });

      this.ws.addEventListener("message", (event) => {
        log.debug("+WebsocketManager_addEventListener(TRACE)");
        log.info(
          "WebsocketManager_addEventListener() => Received messaged, routing to handler"
        );
        this.routeMessageToHandler(event.data);
        log.debug("-WebsocketManager_addEventListener(TRACE)");
      });
    });
    log.debug("-WebsocketManager_constructor(TRACE)");
  }

  handleKioskUnlockEvent(payload) {
    log.debug("+WebsocketManager_handleKioskUnlockEvent(TRACE)");
    log.info(
      "WebsocketManager_handleKioskUnlockEvent() => payload: %s",
      JSON.stringify(payload)
    );
    this.kioskUnlockEventHandlerMap.get(payload["event"])(payload["message"]);
    log.debug("-WebsocketManager_handleKioskUnlockEvent(TRACE)");
  }

  storeDeviceTerminalID(payload) {
    log.debug("+WebsocketManager_storeDeviceTerminalID(TRACE)");
    log.info(
      "WebsocketManager_storeDeviceTerminalID() => payload: %s",
      JSON.stringify(payload)
    );
    localStorage.setItem("deviceTerminalID", payload["terminalID"]);
    log.debug("-WebsocketManager_storeDeviceTerminalID(TRACE)");
  }

  getDeviceTerminalID() {
    log.debug("+WebsocketManager_getDeviceTerminalID(TRACE)");
    let value = localStorage.getItem("deviceTerminalID");
    if (value === undefined || value === null || value === "") value = 0;
    log.info("WebsocketManager_getDeviceTerminalID() => value : %s", value);
    log.debug("-WebsocketManager_getDeviceTerminalID(TRACE)");
    return value;
  }

  routeMessageToHandler(messageString) {
    log.debug("+WebsocketManager_routeMessageToHandler(TRACE)");
    try {
      let message = JSON.parse(messageString);
      switch (message["messageType"]) {
        case "kioskUnlockEvent":
          this.handleKioskUnlockEvent(message["payload"]);
          log.info(
            "WebsocketManager_routeMessageToHandler() => Routed message: %s,",
            JSON.stringify(messageString)
          );
          break;
        case "deviceTerminalID":
          this.storeDeviceTerminalID(message["payload"]);
          break;
        default:
          throw new Error("Unhandled message type: " + message["messageType"]);
      }
    } catch (err) {
      log.error(
        "WebsocketManager_routeMessageToHandler() => Error, could not parse message. message: %s, error: %s",
        messageString,
        err.message
      );
    }
    log.debug("-WebsocketManager_routeMessageToHandler(TRACE)");
  }

  unlockWithCredentials(credentials) {
    log.debug("+WebsocketManager_unlockWithCredentials(TRACE)");
    log.info(
      "WebsocketManager_unlockWithCredentials()  => credentials: %o",
      credentials
    );
    this.ws.send(
      JSON.stringify({
        messageType: "unlockWithCredentials",
        payload: {
          cardNumber: credentials.cardNumber,
          wallID: credentials.wallID,
          userType: credentials.userType,
          language: this.getReleaseLanguage(),
        },
      })
    );
    log.info(
      "WebsocketManager_unlockWithCredentials() => sent unlocking message"
    );
    log.debug("-WebsocketManager_unlockWithCredentials(TRACE)");
  }

  getReleaseLanguage() {
    log.debug("+WebsocketManager_getReleaseLanguage(TRACE)");
    let kioskLanguage = localStorage.getItem("kioskLanguage");
    let defaultLanguage = getDefaultLanguage();
    kioskLanguage =
      kioskLanguage === null ||
      kioskLanguage === undefined ||
      kioskLanguage === "" ||
      kioskLanguage.length <= 0
        ? defaultLanguage
        : kioskLanguage;

    log.info(
      "WebsocketManager_getReleaseLanguage() => release language - " +
        kioskLanguage
    );
    log.debug("-WebsocketManager_getReleaseLanguage(TRACE)");
    return kioskLanguage;
  }

  sendLogs(message, level) {
    this.ws.send(
      JSON.stringify({
        messageType: "kioskLog",
        payload: {
          logMessage: message,
          logLevel: level,
        },
      })
    );
  }
}

export default WebSocketManager;
